import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import './index.scss';

import PaperItem from '../PaperItem';
import { textContent, paperCategory } from '../../types/data.global';
import { logoSVG, mountTeaser } from './whales';
import LabFooter from './LabFooter';
import { usePalette } from '../../theme/PaletteContext';


// smooth-scroll to a section of this page (a plain #hash would make the router jump to the top)
const scrollToId = (e: React.MouseEvent, id: string) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};


/* ---------------- teaser: 鹏 → 鲲 → W → 星河 ---------------- */
const Teaser: React.FC = React.memo(() => {
  const ref = useRef<HTMLElement>(null);
  const { theme } = usePalette(); // remounted (keyed) whenever the palette changes
  useEffect(() => {
    if (!ref.current) return;
    return mountTeaser(ref.current, theme.scene);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const word = (i: number, el: React.ReactNode, className?: string) => (
    <span className={className} style={{ '--i': i } as React.CSSProperties}>{el}</span>
  );

  return (
    <section className='teaser' id='whalesTop' ref={ref} aria-label='WHALES Lab'>
      <canvas id='tzCv' role='img' aria-label='Animation: a murmuration of tiny birds drifts across a dusk sky from right to left, pours into the Kun whale, which becomes the WHALES streamline-W mark; the letters HALES rise in and a faint river of stars drifts off.' />
      <h1 className='sr'>WHALES Lab — Where Human and AI coLlaborate for Exploring Science · Society. Florida State University.</h1>
      <div className='tz-slogan' id='tzSlogan'>
        <p className='exp' aria-hidden='true'>
          {word(0, <><b>W</b>here</>)}
          {word(1, <><b>H</b>uman</>)}
          {word(1.5, 'and', 'sep')}
          {word(2, <><b>A</b>I</>)}
          {word(3, <>co<b>L</b>laborate</>)}
          <i className='br' />
          {word(3.5, 'for', 'sep')}
          {word(4, <><b>E</b>xploring</>)}
          {word(5, <><b>S</b>cience</>)}
          {word(5.5, '·', 'sep')}
          {word(6, <><b>S</b>ociety</>)}
        </p>
        <p className='aff'>Department of Computer Science · Florida State University</p>
        <div className='cta'>
          <a className='btn sun' href='#publications' onClick={(e) => scrollToId(e, 'publications')}>Publications</a>
          <Link className='btn ghost' to='/recruitingPage'>Join us</Link>
        </div>
      </div>
      <div className='tz-ctrl'><button type='button' id='tzReplay'>Replay</button></div>
      <div className='tz-scroll' aria-hidden='true' />
    </section>
  );
});


/* ---------------- about ---------------- */
// the name story, in English and Chinese (WHALES_Brand_Round15, pp. 02–05; English as in the manual's EN version)
type Lang = 'en' | 'zh';
const STORY = {
  en: {
    imagesTitle: 'The mark · four images',
    lead: ['Look up to the stars, and see a boundless heaven and earth;', 'Kun becomes Peng, and see all things dwelling together;', 'Dive the deep sea, and see discovery without end;', 'Take W as a name, and see no limit to human and machine mind.'],
    end: 'Up to the river of stars, down to the deep sea; many minds living as one, seeking without bound.',
  },
  zh: {
    imagesTitle: '标志的含义',
    lead: ['仰望星辰，见天地之无际；', '化鲲为鹏，见万物之同栖；', '深潜沧海，见发现之不息；', '以 W 为名，见人智之无极。'],
    end: '上穷星汉，下探沧海；万智共生，求索无疆。',
  },
};
// the four images: [tile, numeral, EN label, ZH label, EN title, ZH title, EN line, ZH line, motto]
const IMAGES: { art: 'cosmos' | 'flock' | 'dive' | 'w', no: string, en: string[], zh: string[], motto: string }[] = [
  { art: 'flock', no: 'I', en: ['Kun & Peng', 'The Kun becomes the Peng', 'The northern fish becomes the bird; ten thousand wings rise as one, gathering many minds for the long voyage.'], zh: ['鲲鹏', '鲲鹏之变', '北冥化鹏，万羽齐翔，聚众智而远航。'], motto: 'Many minds, moving together.' },
  { art: 'cosmos', no: 'II', en: ['Cosmos', 'The whale among the stars', 'It swims the river of stars, its tail trailing light, sounding a universe without bound.'], zh: ['星辰', '星辰之鲸', '鲸游星汉，尾曳流光，探宇宙之无疆。'], motto: 'Exploring the unknown — beneath us, or beyond us.' },
  { art: 'dive', no: 'III', en: ['The whale', 'The whale of the deep', 'It dives into the dark deep; a whale’s fall feeds ten thousand lives, and new knowledge runs long.'], zh: ['鲸', '深海之鲸', '潜入沧溟，鲸落万生，启新知之绵长。'], motto: 'Go deep. Surface with insight.' },
  { art: 'w', no: 'IV', en: ['The letter', 'A single letter W', 'One form holds a thousand meanings; human and machine complete each other, seeking together the light of truth.'], zh: ['W', '一字之 W', '形蕴万象，人机相济，共求真理之光。'], motto: 'Where Humans & AI Learn · Explore · See.' },
];
// the four images in one row: circles of different sizes, each with a mesh-gradient crescent behind it; the meaning of each sits in a tooltip
const FourImages = ({ lang, tiles }: { lang: Lang, tiles: Record<string, string> }) => {
  const items = IMAGES.map(im => {
    const [label, title, line] = im[lang];
    return (
      <figure className='fi-item' key={im.art} tabIndex={0} aria-label={`${im.no} · ${label}: ${title}`}>
        <div className={`fi-vis fi-${im.art} ${im.art === 'w' ? 'lt' : 'dk'}`}>{tiles[im.art] && <img src={tiles[im.art]} alt='' />}</div>
        <figcaption className='fi-cap'><span className='fi-no'>{im.no}</span>{label}</figcaption>
        <div className='fi-tip' role='tooltip'>
          <h4>{title}</h4>
          <p>{line}</p>
          <p className='motto'>{im.motto}</p>
        </div>
      </figure>
    );
  });

  return (
    <div className='fi'>
      <div className='fi-row'>{items}</div>
    </div>
  );
};

const NameStory: React.FC = () => {
  const { theme } = usePalette();
  const [lang, setLang] = useState<Lang>('en'); // English by default; the switch in the pop-up's header shows the Chinese
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const t = STORY[lang];
  // the four tiles are big SVGs: draw them only once the story is opened, as images
  const tiles = useMemo(() => opened
    ? Object.fromEntries(IMAGES.map(im => {
        let svg = logoSVG(im.art, 'dark');
        // Kun & Peng runs across the whole wide tile: frame a square around its first three figures instead of the centre
        if (im.art === 'flock') svg = svg.replace('viewBox="0 0 400 225"', 'viewBox="-15 -75 365 365"');
        return [im.art, 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)];
      }))
    : {} as Record<string, string>, [opened, theme.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // while the pop-up is open: Esc closes it, the page behind does not scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', onKey); };
  }, [open]);

  const show = () => { setOpened(true); setOpen(true); };

  return (
    <>
      <p className='name-story'>
        <span className='ns-q'>Why “WHALES”?</span>{' '}
        <button type='button' className='ns-btn' aria-haspopup='dialog' onClick={show}>Read the story behind the name</button>
      </p>
      {open && createPortal(
        <div className='whales-page whales-lab ns-modal' role='dialog' aria-modal='true' aria-labelledby='nsTitle'>
          <div className='ns-backdrop' onClick={() => setOpen(false)} />
          <div className={`ns-panel ns-body lang-${lang}`} lang={lang === 'zh' ? 'zh-CN' : 'en'}>
            <div className='ns-top'>
              <h3 id='nsTitle'>{lang === 'zh' ? '为什么叫 WHALES？' : 'Why “WHALES”?'}</h3>
              <div className='ns-lang' role='group' aria-label='Language'>
                <button type='button' aria-pressed={lang === 'en'} onClick={() => setLang('en')}>EN</button>
                <button type='button' aria-pressed={lang === 'zh'} onClick={() => setLang('zh')}>中文</button>
              </div>
              <button type='button' className='ns-close' ref={closeRef} aria-label='Close' onClick={() => setOpen(false)}>×</button>
            </div>

            <p className='ns-h'>{t.imagesTitle}</p>
            <div className='lead4'>{t.lead.map(l => <p key={l}>{l}</p>)}</div>
            <FourImages lang={lang} tiles={tiles} />
            <p className='end'>{t.end}</p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/* ---------------- three fields: the Venn diagram beside the about text ---------------- */
// three equal circles; where all three overlap: our work
const FIELDS = [
  { id: 'vis', cx: 478, cy: 320, label: ['Data Visualization', '(VIS)'], lx: 478, ly: 205 },
  { id: 'sci', cx: 622, cy: 560, label: ['Science'], lx: 716, ly: 606 },
  { id: 'hai', cx: 335, cy: 560, label: ['Human–AI', 'Interaction', '(HCI · AI)'], lx: 258, ly: 560 },
];
const R = 232;

const Venn: React.FC = () => (
    <svg viewBox='95 80 770 720' preserveAspectRatio='xMidYMid meet' role='img' aria-label='Venn diagram of three fields: Data Visualization (VIS), Human–AI Interaction (HCI · AI) and Science. Where all three overlap: our work.'>
      {FIELDS.map(f => <circle key={f.id} className='venn-c' cx={f.cx} cy={f.cy} r={R} />)}
      {FIELDS.map(f => (
        <text key={f.id} className='venn-t' x={f.lx} y={f.ly} textAnchor='middle'>
          {f.label.map((l, i) => <tspan key={i} x={f.lx} dy={i ? 46 : 0}>{l}</tspan>)}
        </text>
      ))}
      <text className='venn-mid' x='478' y='505' textAnchor='middle'>Our Work</text>
    </svg>
);

const AboutSection: React.FC = () => (
  <section className='blk' id='about'>
    <div className='wrap'>
      {/* a grid so the circles span exactly the two paragraphs and the caption sits level with "Why WHALES?" */}
      <div className='about rv'>
        <p className='eyebrow about-eye'><b>01</b> About the lab</p>
        <div className='body about-body'>
            <p>WHALES Lab is a research group in the Department of Computer Science at Florida State University, led by <Link to='/'>Dr. Yifang Wang</Link>. We work at the intersection of data visualization, human–AI collaboration and human–computer interaction, together with collaborators in the science of science, computational social science and management.</p>
            <p>We reimagine how humans and AI think together — shaping the next generation of human–AI interaction and building practical systems that put it to work for science, where humans bring expertise and judgment and AI brings efficiency and scale. Our work follows two loops: <b>how humans and AI work together</b> to make scientific discoveries (discovery loop), and <b>how science itself is organized and supported</b> (governance loop), so that research leaders and funders can make better, data-driven decisions.</p>
        </div>
        <div className='about-art' role='figure' aria-label='Three fields'><Venn /></div>
        <div className='about-why'><NameStory /></div>
      </div>
    </div>
  </section>
);


/* ---------------- publications: every paper of the homepage's research list ---------------- */
const PUB_TAGS = [paperCategory.AI4Sci, paperCategory.LLM, paperCategory.VA, paperCategory.CSS, paperCategory.HEALTH, paperCategory.DR, paperCategory.DH];
const yearOf = (p: any) => +(String(p.venueShort).match(/\d{4}/)?.[0] || 0);
const Publications: React.FC = () => {
  const papers = textContent.researchPage as any[];
  const tags = [paperCategory.FEATURED, ...PUB_TAGS].filter(t => papers.some(p => p.category?.includes(t)));
  const [filter, setFilter] = useState<string>(paperCategory.FEATURED);
  const list = papers.filter(p => filter === paperCategory.ALL || p.category?.includes(filter));
  const years = Array.from(new Set(list.map(yearOf))).sort((a, b) => b - a);

  return (
    <div className='pp-pubs'>
      <div className='sec-head rv'>
        <div><h2 className='eyebrow'><b>02</b> Research</h2></div>
        <p className='side'><a href='https://scholar.google.com/citations?user=3qWVVUwAAAAJ&hl=en' target='_blank' rel='noreferrer'>Full list on Google Scholar ↗</a></p>
      </div>
      <div className='filters' role='group' aria-label='Filter publications'>
        {[paperCategory.ALL, ...tags].map(t => (
          <button key={t} type='button' aria-pressed={filter === t} onClick={() => setFilter(t)}>{t}</button>
        ))}
      </div>
      <p className='pub-note'>* corresponding author · † student under Yifang’s supervision · ‡ equal contribution</p>
      <div>
        {years.map(y => (
          <div className='year' key={y}>
            <h3>{y}</h3>
            <div className='pubs'>
              {list.filter(p => yearOf(p) === y).map(p => <PaperItem key={p.title} paper={p} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


/* ---------------- people: one column of small round portraits (the ring colour says the role) ---------------- */
const shortAff = (s = '') => s
  .replace(/\s*\(.+\)\s*$/, '') // institution only: drop the degree / major in brackets
  .replace(/(The )?Hong Kong University of Science and Technology/, 'HKUST')
  .replace('The University of Hong Kong', 'HKU')
  .replace(/University of Wisconsin[-–]Madison/, 'UW–Madison')
  .replace('Washington University in St. Louis', 'WashU');
const roleName = (s = '') => s.replace(/\bPhD\b/, 'Ph.D.');
const initials = (n: string) => n.replace(/\(.*?\)/g, '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('');

const Portrait = ({ img, name }: { img?: string, name: string }) => {
  const [broken, setBroken] = useState(false);
  return img && !broken
    ? <img src={img} alt={name} loading='lazy' decoding='async' onError={() => setBroken(true)} />
    : <span className='avatar'>{initials(name)}</span>;
};

// a cute, half-transparent Halloween ghost worn over each portrait like a hood: its dome follows the top of the circle
// (a little larger), its body drifts off to one side and its hem sways below the circle; a soft gradient and a highlight
// give it some volume. The photo shows faintly through it (hover a member to see the photo). Coordinates: the portrait
// circle is 0..100; `i` staggers the timing so the ghosts don't move in step.
const HEM_A = 'M-2 52 A52 52 0 0 1 102 49 C105 72 110 92 122 105 Q111 116 99 108 Q88 118 76 109 Q64 119 52 110 Q40 120 28 110 Q16 119 4 109 Q-4 113 -7 104 C-5 88 -3 70 -2 52 Z';
const HEM_B = 'M-2 52 A52 52 0 0 1 102 49 C105 73 108 95 117 111 Q108 104 97 112 Q86 104 75 113 Q63 105 51 114 Q39 106 27 113 Q15 105 3 111 Q-6 109 -5 100 C-4 86 -3 70 -2 52 Z';
const Ghost = ({ i }: { i: number }) => {
  const d = `${-(i * 0.37) % 2.6}s`;
  const id = React.useId().replace(/:/g, '');
  return (
    <svg className='ghost' viewBox='-14 -6 140 132' aria-hidden='true' style={{ animationDelay: d }}>
      <defs>
        <radialGradient id={`gb${id}`} cx='34%' cy='22%' r='85%'>
          <stop offset='0%' stopColor='#FFFFFF' stopOpacity='.9' />
          <stop offset='55%' stopColor='#F4F1FB' stopOpacity='.66' />
          <stop offset='100%' stopColor='#B9B0D6' stopOpacity='.62' />
        </radialGradient>
        <filter id={`gs${id}`} x='-20%' y='-20%' width='140%' height='140%'><feGaussianBlur stdDeviation='3' /></filter>
      </defs>
      <g className='ghost-arm ghost-arm-l' style={{ animationDelay: d }}><path d='M0 66 Q-12 60 -13 47 Q-7 50 -5 45 Q-2 56 4 59 Z' /></g>
      <g className='ghost-arm ghost-arm-r' style={{ animationDelay: d }}><path d='M100 64 Q113 57 115 43 Q109 46 107 41 Q104 53 97 57 Z' /></g>
      <path className='ghost-body' d={HEM_A} fill={`url(#gb${id})`}>
        <animate attributeName='d' dur='2.6s' begin={d} repeatCount='indefinite' values={`${HEM_A};${HEM_B};${HEM_A}`} calcMode='spline' keySplines='.45 0 .55 1;.45 0 .55 1' />
      </path>
      {/* a soft sheen on the upper left and a shade along the lower right, for a rounded, 3D look */}
      <ellipse className='ghost-sheen' cx='28' cy='20' rx='17' ry='10' transform='rotate(-28 28 20)' filter={`url(#gs${id})`} />
      <path className='ghost-shade' d='M96 60 C99 78 104 94 114 104 Q104 108 92 106 C95 90 96 74 96 60 Z' filter={`url(#gs${id})`} />
      <ellipse className='ghost-eye' cx='40' cy='40' rx='3.4' ry='4.2' />
      <ellipse className='ghost-eye' cx='62' cy='40' rx='3.4' ry='4.2' />
      <circle className='ghost-glint' cx='41.2' cy='38.4' r='1.1' />
      <circle className='ghost-glint' cx='63.2' cy='38.4' r='1.1' />
      <ellipse className='ghost-blush' cx='31' cy='50' rx='5.6' ry='3.2' />
      <ellipse className='ghost-blush' cx='71' cy='50' rx='5.6' ry='3.2' />
      <path className='ghost-mouth' d='M45 48 Q51 58 57 48 Q51 51 45 48 Z' />
    </svg>
  );
};

const Member = ({ m, ring, role, aff, topics, extra, i = 0 }: { m: any, ring: string, role?: string, aff?: string, topics?: string, extra?: React.ReactNode, i?: number }) => (
  <div className='mem rv'>
    <span className={`av-wrap r-${ring}`}><span className='av'><Portrait img={m.img} name={m.name} /></span><Ghost i={i} /></span>
    <div>
      <p className='nm'><span className='nmn'>{m.homepageUrl ? <a href={m.homepageUrl} target='_blank' rel='noreferrer'>{m.name}</a> : m.name}</span>{role && <> <span className='rl'>({role})</span></>}</p>
      {aff && <p className='af'>{aff}</p>}
      {topics && <p className='tp'>{topics}</p>}
      {extra}
    </div>
  </div>
);

const People: React.FC = () => {
  const lab = textContent.labMemberPage as any;
  const pi = lab.currentMembers.find((m: any) => m.position === 'Principle Investigator');
  // the ring colour follows the role: Ph.D. student · visiting researcher · research intern
  const kindOf = (m: any) => /Visiting/i.test(m.position) ? 'vphd' : /Intern/i.test(m.position) ? 'ra' : 'phd';
  const members = [...lab.currentMembers.filter((m: any) => m !== pi), ...lab.internMembers, ...lab.collaborators]
    .map((m: any) => ({ m, k: kindOf(m) }));
  const rank = (name: string) => { const i = lab.memberOrder.indexOf(name); return i < 0 ? Infinity : i; };
  members.sort((a, b) => rank(a.m.name) - rank(b.m.name));

  return (
    <aside className='pp-people' id='people' aria-label='People'>
      <div className='sec-head rv'><div><h2 className='eyebrow'><b>03</b> People</h2></div></div>
      <div className='members'>
        <p className='mg'>Principal investigator</p>
        <Member m={pi} ring='pi' aff='Principal Investigator' />
        <p className='mg'>Lab members</p>
        {members.map(({ m, k }, i) => (
          <Member key={m.name} m={m} ring={k} i={i + 1} role={roleName(m.position)} aff={shortAff(m.institution)} topics={m.specialty} />
        ))}
        {(lab.labCats || []).map((m: any, i: number) => (
          <Member key={m.name} m={m} ring='cat' i={members.length + i + 1} role={m.position} aff={m.institution} />
        ))}
      </div>
      <h3 className='alum-h'>Alumni &amp; previous mentees</h3>
      <ul className='alumni'>
        {lab.formerCollaboratedStudents.map((a: any) => (
          <li key={a.name}>
            <b>{a.homepageUrl ? <a href={a.homepageUrl} target='_blank' rel='noreferrer'>{a.name}</a> : a.name}</b>{' '}
            <span className='yr'>{a.role === 'UG student' ? 'Undergraduate' : a.role}{a.year ? ` · ${String(a.year).replace('-', '–')}` : ''}</span>
            <span className='mj'>{shortAff(a.university)} — {a.field}</span>
            {a.now && <span className='now'>Now: {a.now}</span>}
          </li>
        ))}
      </ul>
    </aside>
  );
};


const WhalesLabPage: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { theme } = usePalette(); // the opening animation is rebuilt in the new colours when the palette changes
  const location = useLocation();

  // opened at a section (e.g. /whalesLab#people from the footer elsewhere): go there once the router has scrolled to the top
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 150);
    return () => clearTimeout(t);
  }, [location.hash]);

  // reveal on scroll
  useEffect(() => {
    const els = rootRef.current ? Array.from(rootRef.current.querySelectorAll('.rv')) : [];
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className='whales-page whales-lab' ref={rootRef}>
      <Teaser key={theme.id} />
      <AboutSection />
      <section className='blk' id='publications'>
        <div className='wrap pp'>
          <Publications />
          <People />
        </div>
      </section>
      <LabFooter />
    </div>
  );
};

export default WhalesLabPage;
